import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import AnimatedMainContainer from "../../components/AnimatedMainContainer";
import useNotification from "../../hooks/newUXNotification";
import FormButton from "../../components/FormButton";
import { ReportMultiSelect } from "./styles";
import {
  startContractualProductsReportService,
  getContractualProductsReportStatusService,
} from "../../service/contractualProducts.service";
import {
  getReportFilterEventsByYearService,
  getReportFilterYearsService,
} from "../../service/reportFilter.service";
import {
  Container,
  FormContainer,
  TitleContainer,
  Title,
  FieldsContainer,
  FilterActionsContainer,
  SubmitContainer,
  GoBackContainer,
  ActionContainer,
  SelectContainer,
  Label,
} from "./styles";

const AdminContractualProductsReport = () => {
  const { token } = useSelector((state) => state.auth);
  const intl = useIntl();
  const history = useHistory();
  const notification = useNotification();
  const [loading, setLoading] = useState(false);
  const [years, setYears] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedYear, setSelectedYear] = useState(undefined);
  const [selectedEventIdList, setSelectedEventIdList] = useState([]);

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await getReportFilterYearsService({ token });
        const yearList = response.data?.output || [];

        setYears(
          yearList.map((year) => ({ value: year, label: String(year) }))
        );
      } catch (error) {
        console.error("Erro ao buscar anos:", error);
        notification.displayError(
          intl.formatMessage({ id: "error.fetching_events" }),
          "fetch-years-error"
        );
      }
    };

    if (token) {
      fetchYears();
    }
  }, [token]);

  const handleChangeYear = async (year) => {
    setSelectedYear(year);
    setSelectedEventIdList([]);
    setEvents([]);

    if (year === undefined || year === null) {
      return;
    }

    try {
      const response = await getReportFilterEventsByYearService({ token, year });
      const eventsList = response.data?.output || [];

      setEvents(
        eventsList.map((event) => ({
          value: event.idNumber,
          label: event.eventEditionName,
        }))
      );
    } catch (error) {
      console.error("Erro ao buscar eventos:", error);
      notification.displayError(
        intl.formatMessage({ id: "error.fetching_events" }),
        "fetch-events-error"
      );
    }
  };

  const downloadFile = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const pollReportStatus = async (reportId, maxAttempts = 40, interval = 5000) => {
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await getContractualProductsReportStatusService(token, reportId);
        const { status, downloadUrl } = response.data;

        if (status === "PROCESSED") {
          if (downloadUrl) {
            await downloadFile(downloadUrl);
            notification.displaySuccess(
              intl.formatMessage({ id: "contractual_products_report.success" }),
              "report-generated-success"
            );
          }
          return response.data;
        }

        if (status === "ERROR") {
          throw new Error("PROCESSING_ERROR");
        }

        if (status === "WAITING" || status === "PENDING") {
          await new Promise((resolve) => setTimeout(resolve, interval));
          attempts++;
          continue;
        }

        throw new Error(`UNKNOWN_STATUS_${status}`);
      } catch (error) {
        if (error.message.includes("STATUS") || error.message === "PROCESSING_ERROR") {
          throw error;
        }
        // Se for erro de rede, tenta novamente
        attempts++;
        await new Promise((resolve) => setTimeout(resolve, interval));
      }
    }

    throw new Error("TIMEOUT");
  };

  const handleExport = async () => {
    if (selectedYear === undefined || selectedYear === null) {
      notification.displayError(
        intl.formatMessage({ id: "report_filter_year_required" }),
        "year-required-error"
      );
      return;
    }

    if (!selectedEventIdList || selectedEventIdList.length === 0) {
      notification.displayError(
        intl.formatMessage({ id: "report_filter_event_required" }),
        "event-required-error"
      );
      return;
    }

    try {
      setLoading(true);

      const data = {
        eventIdList: selectedEventIdList,
      };

      const response = await startContractualProductsReportService(token, data);
      const { reportId, error } = response.data;

      if (error || !reportId) {
        throw new Error("Erro ao iniciar processamento do relatório");
      }

      await pollReportStatus(reportId);
    } catch (error) {
      console.error("Erro ao exportar relatório:", error);
      
      let errorMessage = "error.generic";
      
      if (error.message === "TIMEOUT") {
        errorMessage = "error.report_timeout";
      } else if (error.message === "PROCESSING_ERROR") {
        errorMessage = "error.report_processing";
      } else if (error.response?.data?.code) {
        errorMessage = `api.error.${error.response.data.code}`;
      }

      notification.displayError(
        intl.formatMessage({ id: errorMessage }),
        "export-report-error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <AnimatedMainContainer>
      <Container>
        <TitleContainer>
          <Title>
            <FormattedMessage id="contractual_products_report_for_partners" />
          </Title>
        </TitleContainer>

        <FormContainer>
          <FieldsContainer>
            <SelectContainer>
              <Label>
                <FormattedMessage id="year" />
                <span style={{ color: "red" }}>*</span>
              </Label>
              <ReportMultiSelect
                placeholder={intl.formatMessage({ id: "year" })}
                options={years}
                value={selectedYear}
                onChange={handleChangeYear}
                allowClear
                disabled={loading}
                style={{ width: "100%" }}
                showArrow
              />
            </SelectContainer>
            <SelectContainer>
              <Label>
                <FormattedMessage id="events" />
                <span style={{ color: "red" }}>*</span>
              </Label>
              <ReportMultiSelect
                mode="multiple"
                placeholder={intl.formatMessage({ id: "select_events" })}
                options={events}
                value={selectedEventIdList}
                onChange={setSelectedEventIdList}
                allowClear
                disabled={loading || !selectedYear}
                style={{ width: "100%" }}
                showArrow
                notFoundContent={intl.formatMessage({
                  id: "report_filter_no_events_for_year",
                })}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </SelectContainer>
          </FieldsContainer>

          <FilterActionsContainer>
            <ActionContainer>
              <GoBackContainer>
                <FormButton
                  disabled={loading}
                  onClick={handleGoBack}
                  size="large"
                  type="cancel"
                >
                  <FormattedMessage id="goback" />
                </FormButton>
              </GoBackContainer>
              <SubmitContainer>
                <FormButton
                  disabled={loading}
                  onClick={loading ? undefined : handleExport}
                  size="large"
                  type="submit"
                  loading={loading}
                >
                  {loading ? (
                    <FormattedMessage id="contractual_products_report.processing" />
                  ) : (
                    <FormattedMessage id="export" />
                  )}
                </FormButton>
              </SubmitContainer>
            </ActionContainer>
          </FilterActionsContainer>
        </FormContainer>
      </Container>
    </AnimatedMainContainer>
  );
};

export default AdminContractualProductsReport;
